/**
 * KycVariant — parametriza el flujo de captura KYC (`IneCaptureStep` +
 * `SelfieStep`) para reusarlo entre el solicitante PF (`applicant`) y el
 * representante legal de Persona Moral (`legal_rep`).
 *
 * GAP 3 del doc `docs/plans/persona-moral/09-fe-gaps-vs-412-2026-06-08.md`:
 * la captura del rep reusa al 100% los componentes KYC de PF; lo que cambia es
 * (1) el `subjectType` que viaja en `kyc/start`, (2) los destinos de navegación
 * entre sub-steps y (3) **las claves de los bloques de la sesión**.
 *
 * (3) se daba por sentado al revés: se asumía que en una sesión moral el bloque
 * `kyc` del BE ERA el del representante, así que los reads no cambiaban. Falso
 * — una sesión PM devuelve `kyc_admin` con steps sufijados `_admin`
 * (`ine_capture_admin`, `selfie_admin`, …) y no expone ningún bloque `kyc`.
 * Con las claves de PF, TODOS los reads daban `undefined`: tras un upload
 * exitoso, `SelfieStep` leía "INE no completado" y rebotaba a la captura, que
 * remontaba el SDK y pedía recapturar; el segundo POST moría con 422 "este paso
 * ya ha sido completado". Por eso las claves viven aquí, junto al resto de lo
 * que distingue a los dos sujetos.
 *
 * El default es `APPLICANT_KYC_VARIANT` — omitir el prop deja el flujo PF
 * idéntico, sin regresión por construcción.
 *
 * > **Zona de riesgo**: el cableado `legal_rep` no se verifica en localhost
 * > (JAAK CORS-rechaza localhost). Validar E2E en staging antes de encender
 * > `onboarding_moral_enabled`. Ver Slice 3 del doc 09.
 */
export type KycSubjectType = 'applicant' | 'legal_rep';

/**
 * Claves con las que este sujeto aparece en `session.blocks`. El BE nombra el
 * bloque y sus steps distinto según de quién sea el KYC (PF vs representante
 * legal PM), así que leerlos hardcodeados hace que el flujo del otro sujeto vea
 * todo como no completado.
 */
export interface KycBlockKeys {
  /** Bloque que agrupa los sub-steps de captura. */
  block: string;
  /** Step de captura del documento (INE frente + reverso). */
  ineStep: string;
  /** Step de selfie biométrica. */
  selfieStep: string;
  /** Step de permisos de ubicación y cámara. */
  locationStep: string;
}

export interface KycVariant {
  /** Viaja en el body de `kyc/start`; el BE asume `applicant` si se omite. */
  subjectType: KycSubjectType;
  /** Cómo leer el avance de este sujeto en `session.blocks`. */
  blockKeys: KycBlockKeys;
  paths: {
    /** Ruta del step de captura INE (frente + reverso en una sola pantalla). */
    ine: string;
    /** Ruta del step de selfie. */
    selfie: string;
    /** Destino tras completar el selfie (result PF / siguiente step PM). */
    result: string;
    /** Destino del botón VOLVER desde el step INE. */
    back: string;
  };
  /**
   * Override de claves i18n del copy de captura. Permite contextualizar el
   * texto al sujeto ("del representante" para PM) sin tocar el flujo PF: si se
   * omite, los steps caen a las claves `auth:identity.*` de siempre. Las claves
   * van namespaced (`onboardingMoral:...`) y los steps cargan ambos namespaces.
   */
  copy?: {
    frontTitle?: string;
    frontInstruction?: string;
    backTitle?: string;
    backInstruction?: string;
    /**
     * Copy mientras el SDK captura. NO es "la segunda cara": el SDK captura
     * ambas dentro del mismo estado y dirige cada lado con su propio overlay,
     * así que este texto es neutral a propósito.
     */
    captureTitle?: string;
    captureInstruction?: string;
    selfieTitle?: string;
    selfieInstruction?: string;
    permissionDescription?: string;
  };
}

/** Flujo PF (default). Mantiene el comportamiento histórico de `/kyc/*`. */
export const APPLICANT_KYC_VARIANT: KycVariant = {
  subjectType: 'applicant',
  blockKeys: {
    block: 'kyc',
    ineStep: 'ine_capture',
    selfieStep: 'selfie',
    locationStep: 'location_and_camera',
  },
  paths: {
    ine: '/kyc/ine-capture',
    selfie: '/kyc/selfie',
    result: '/kyc/identity-result',
    back: '/register/identity',
  },
};

/** Flujo del representante legal PM (GAP 3 #412). Detrás del flag moral. */
export const LEGAL_REP_KYC_VARIANT: KycVariant = {
  subjectType: 'legal_rep',
  // Nombres reales del contrato PM (moral_v1.yml), verificados contra una
  // sesión de dev: bloque `kyc_admin`, steps sufijados `_admin`.
  blockKeys: {
    block: 'kyc_admin',
    ineStep: 'ine_capture_admin',
    selfieStep: 'selfie_admin',
    locationStep: 'location_and_camera_admin',
  },
  paths: {
    ine: '/onboarding/legal-rep-ine-front',
    selfie: '/onboarding/legal-rep-selfie',
    result: '/onboarding/legal-rep-data',
    back: '/onboarding/legal-rep-consent',
  },
  // Copy contextualizado al representante legal (vs. el "tu INE/identidad" de PF).
  copy: {
    frontTitle: 'onboardingMoral:legalRepKyc.front.title',
    frontInstruction: 'onboardingMoral:legalRepKyc.front.instruction',
    backTitle: 'onboardingMoral:legalRepKyc.back.title',
    backInstruction: 'onboardingMoral:legalRepKyc.back.instruction',
    captureTitle: 'onboardingMoral:legalRepKyc.capture.title',
    captureInstruction: 'onboardingMoral:legalRepKyc.capture.instruction',
    selfieTitle: 'onboardingMoral:legalRepKyc.selfie.title',
    selfieInstruction: 'onboardingMoral:legalRepKyc.selfie.instruction',
    permissionDescription: 'onboardingMoral:legalRepKyc.permissionDescription',
  },
};

/** Shape laxo para leer bloques cuyas claves varían por sujeto (`OnboardingBlocks`
 *  tipa las de PF, pero una sesión PM trae `kyc_admin` y steps sufijados). */
type LooseBlocks = Record<string, { status?: string; steps?: Record<string, { status?: string }> }>;

/**
 * Lee el estado de los sub-steps de KYC del sujeto que describe `variant`.
 * Punto ÚNICO de acceso: si se lee `blocks` a mano con claves fijas, el flujo
 * del otro sujeto vuelve a ver todo como no completado (ver el porqué arriba).
 *
 * Devuelve `undefined` en cada campo cuando el bloque no viene en la respuesta,
 * para que el caller distinga "no completado" de "no sé" si lo necesita.
 */
export function readKycStepStatuses(
  blocks: unknown,
  variant: KycVariant
): {
  blockStatus: string | undefined;
  stepNames: string[];
  ineDone: boolean;
  selfieDone: boolean;
  locationDone: boolean;
} {
  const { block, ineStep, selfieStep, locationStep } = variant.blockKeys;
  const kycBlock = (blocks as LooseBlocks | undefined)?.[block];
  const steps = kycBlock?.steps;
  return {
    blockStatus: kycBlock?.status,
    stepNames: steps ? Object.keys(steps) : [],
    ineDone: steps?.[ineStep]?.status === 'completed',
    selfieDone: steps?.[selfieStep]?.status === 'completed',
    locationDone: steps?.[locationStep]?.status === 'completed',
  };
}
